/* Erwal — rustig filteren op de collectie- en zoekpagina's.
 *
 * Eén taak: na een filterwissel schuift de pagina zacht naar de pillenrij, zodat
 * je de categorieen, je gekozen filters en de eerste producten in één blik hebt.
 * Een harde sprong voelt als een herlaad, en dat is het juist niet: de sectie
 * wordt via ajax bijgewerkt, de pagina zelf blijft staan.
 *
 * Wat hier eerder misging, zodat het niet terugkomt:
 *
 *  - Het vastzetten van de filterhoogtes tijdens de wissel is eruit. Dat was een
 *    pleister op de witruimte die Horizons eigen accordeon veroorzaakt; die is
 *    nu bij de bron opgelost, in erwal-custom.css. Zie daar het kader
 *    "Filterkolom: geen hoogte-animatie".
 *  - Horizon vervángt het productraster niet, het morpht het bij
 *    (assets/morph.js, aangeroepen vanuit assets/section-renderer.js). #ResultsList
 *    blijft dus dezelfde node — gemeten 17 sep 2026. Wachten op een nieuw raster
 *    liep daarom altijd in de noodklok van 3 seconden. We wachten nu tot de
 *    mutaties ophouden.
 *  - In Horizon scrollt het vénster niet. html en body staan op overflow hidden;
 *    het schuiven gebeurt in .page-wrapper (op smalle schermen is het wél het
 *    document). Zoek dus eerst het echte scrollvak op.
 *  - scrollTo met behavior 'smooth' doet in dat vak niets (gemeten: stand 2200,
 *    opdracht naar 1100, nog steeds 2200). Daarom animeren we zelf, per beeldje,
 *    en is elke stap expliciet 'instant'.
 *
 * Shopify's eigen gebeurtenissen (collectionUpdate / searchUpdate) komen op de
 * collectiepagina niet altijd aan, dus we luisteren naar een change op een
 * filterveld.
 *
 *  - 18 sep 2026, onderzoek in vier lenzen: Horizon bindt zijn eigen
 *    change-listener eerder op document dan wij (component.js, capture, al bij
 *    de header), en zodra de nieuwe sectie al in de cache zit (na een
 *    hover-prefetch van 200 ms, of bij het terugvinken van een eerdere stand)
 *    loopt de morph in de microtask-pauze tússen Horizons listener en de onze.
 *    Ons change-event mat de kaart dan pas ná de sprong en zette .erwal-stil te
 *    laat. Daarom hangt de bescherming nu niet meer aan het event maar om de
 *    render zelf: een wrapper om sectionRenderer.renderSection, dezelfde
 *    instantie die facets.js gebruikt. Dat werkt ongeacht cache, listenervolgorde
 *    of invoerbron (muis, toetsenbord, chips, "Wissen").
 */
import { sectionRenderer } from '@theme/section-renderer';

(() => {
  /* Stempel om te kunnen zien welke versie de winkel serveert; een string
     overleeft de minificatie van Shopify, een variabelenaam niet. */
  window.__erwalFilterVersie = '21sep-popup-o';

  const scrollvak = () => {
    const wrapper = document.querySelector('.page-wrapper');
    if (wrapper) {
      const stijl = getComputedStyle(wrapper);
      if (/(auto|scroll)/.test(stijl.overflowY) && wrapper.scrollHeight > wrapper.clientHeight + 4) {
        return wrapper;
      }
    }
    return document.scrollingElement || document.documentElement;
  };

  const zichtbaar = (el) => el && el.offsetParent !== null && el.getBoundingClientRect().height > 0;

  /* Ruimte die bovenaan bezet is: de vastgeklikte kop, plus een filterbalk die
     daar ook blijft plakken. */
  const ruimteBoven = () => {
    const kop = document.querySelector('.header-wrapper, .header, header');
    let hoog = kop ? kop.getBoundingClientRect().height : 0;
    const balk = document.querySelector('.facets-controls-wrapper');
    if (balk && getComputedStyle(balk).position === 'sticky') {
      hoog += balk.getBoundingClientRect().height;
    }
    return hoog;
  };

  const zachtNaar = (vak, doelY, duur = 300) => {
    const start = vak.scrollTop;
    const verschil = doelY - start;
    if (Math.abs(verschil) < 4) return;
    /* Let op: de css zet scroll-behavior: smooth op dit vak, en zacht scrollen
       doet hier niets (gemeten 17 sep 2026). Elke stap moet daarom expliciet
       'instant' zijn, anders beweegt er helemaal niets. */
    const zet = (y) => vak.scrollTo({ top: y, behavior: 'instant' });
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      zet(doelY);
      return;
    }
    /* Met een tijdklok in plaats van requestAnimationFrame: rAF staat stil in
       een tabblad dat niet tekent, en dan zou er helemaal niets gebeuren. */
    const t0 = performance.now();
    const klok = setInterval(() => {
      const p = Math.min(1, (performance.now() - t0) / duur);
      const soepel = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
      zet(start + verschil * soepel);
      if (p >= 1) clearInterval(klok);
    }, 16);
  };

  const paneelOpen = () =>
    !!document.querySelector('dialog[open]:not(.ewp), theme-drawer[open], .menu-drawer-container[open]');

  /* Op telefoons vink je aan terwijl het paneel openstaat; dan ligt de scroll
     vast en doet een scrollopdracht niets. Wachten tot het dicht is. */
  const zodraDicht = (fn) => {
    if (!paneelOpen()) return fn();
    let pogingen = 0;
    const t = setInterval(() => {
      if (!paneelOpen() || ++pogingen > 60) {
        clearInterval(t);
        fn();
      }
    }, 150);
  };

  const naarBoven = () => {
    const pillen = document.querySelector('.ecp__row');
    const knop = document.querySelector('.facets-toggle__button');
    const doel = zichtbaar(pillen)
      ? pillen
      : zichtbaar(knop)
        ? knop
        : document.querySelector('#ResultsList') || document.querySelector('.product-grid');
    if (!doel) return;

    const vak = scrollvak();
    const y = vak.scrollTop + doel.getBoundingClientRect().top - ruimteBoven() - 12;
    zachtNaar(vak, Math.max(0, y));
  };

  /* De filterkaart is sticky in een rastercel die zo hoog is als het
     productraster. De kaart is hoger dan het scherm, dus hij zit meestal
     vastgeklemd tegen de onderkant van die cel. Krimpt of groeit het raster
     na een filter, dan schuift die onderkant en springt de hele kaart mee
     (gemeten 18 sep 2026: kaarttop van -776 naar +401). Daarom: vóór de
     wissel onthouden waar de kaart in beeld staat, en direct na de morph
     (in dezelfde microtask-keten, dus vóór het tekenen) de pagina precies
     zoveel verschuiven dat de kaart onder je muis blijft staan. */
  const kaartPositie = () => {
    const kaart = document.querySelector('.facets--vertical');
    if (!kaart || !zichtbaar(kaart)) return null;
    return kaart.getBoundingClientRect().top;
  };

  /* Scrollstand die wij verwachten. Wijkt de echte stand daarvan af, dan heeft
     de bezoeker (of iets anders) intussen gescrold en blijven we van de scroll
     af: een late correctie zou anders zijn eigen wieltik terugdraaien. */
  let scrollVerwacht = null;
  const scrollOngemoeid = () => {
    if (scrollVerwacht === null) return true;
    return Math.abs(scrollvak().scrollTop - scrollVerwacht) < 1;
  };

  const kaartVasthouden = (kaartVoor) => {
    if (kaartVoor === null) return false;
    const kaartNa = kaartPositie();
    if (kaartNa === null) return false;
    const verschil = kaartNa - kaartVoor;
    if (Math.abs(verschil) < 1) return false;
    const vak = scrollvak();
    const max = Math.max(0, vak.scrollHeight - vak.clientHeight);
    /* Afronden: een scrollTop met fractie zet de haarlijnen op een halve
       schermpixel (DPR 2,75) en dat tekent als een dubbele, wazige streep. */
    const doel = Math.round(Math.min(max, Math.max(0, vak.scrollTop + verschil)));
    if (Math.abs(doel - vak.scrollTop) < 1) return false;
    vak.scrollTo({ top: doel, behavior: 'instant' });
    scrollVerwacht = doel;
    return true;
  };

  /* Een mutatiebatch die alleen uit hartjes bestaat (erwal-favorieten zet ze
     150 ms na de morph terug in de kaarten) verandert niets aan de hoogte. */
  const alleenHartjes = (records) =>
    records.every((r) => {
      const nodes = [...r.addedNodes, ...r.removedNodes];
      return nodes.length > 0 && nodes.every((n) => n.nodeType === 1 && n.classList.contains('erwal-fav'));
    });

  /* Desktop: alleen ingrijpen als het beeld onder het (gekrompen) raster is
     uitgekomen. Direct na de morph kan het raster nog de oude hoogte hebben,
     dus we kijken nog even mee met de mutaties, maar nooit meer nadat de
     bezoeker zelf heeft gescrold. */
  const alleenAlsOnderRaster = (kaartVoor) => {
    let klok = null;
    let eind = null;
    let klaar = false;
    const doel = document.getElementById('MainContent') || document.body;
    const stop = () => { klaar = true; waarnemer.disconnect(); clearTimeout(klok); clearTimeout(eind); };
    const check = (laatsteKeer) => {
      if (klaar) return;
      if (!scrollOngemoeid()) { stop(); return; }
      /* Eerst de kaart op zijn plek houden; is dat gebeurd, dan is de rest
         niet meer nodig (de kaart staat waar hij stond, met de nieuwe
         producten ernaast). */
      if (kaartVasthouden(kaartVoor)) { stop(); return; }
      const raster = document.querySelector('#ResultsList') || document.querySelector('.product-grid');
      if (!raster) { if (laatsteKeer) stop(); return; }
      /* Meet op de kaarten, niet op het raster: het rastervak houdt na een morph
         zijn oude hoogte (gemeten 18 sep 2026: 2 kaarten in een vak van 3800 px),
         dus daar lees je nooit af dat de lijst korter is geworden. */
      const kaarten = raster.querySelectorAll('.product-card');
      const eerste = kaarten[0] ? kaarten[0].getBoundingClientRect() : raster.getBoundingClientRect();
      const laatste = kaarten.length ? kaarten[kaarten.length - 1].getBoundingClientRect() : raster.getBoundingClientRect();
      const boven = ruimteBoven();
      /* Eerste kaart uit beeld (boven) én de laatste kaart al in beeld of erboven:
         je kijkt naar de staart of naar leegte. Loopt de lijst nog onder het scherm
         door, dan blijven we van de scroll af. */
      const vak = scrollvak();
      if (eerste.top < boven && laatste.bottom < vak.clientHeight) {
        /* In één keer, in hetzelfde beeldje als de morph (klacht van 30 aug:
           raster krimpt en je staat opeens onder de laatste rij). */
        const r = raster.getBoundingClientRect();
        const y = Math.round(Math.max(0, vak.scrollTop + r.top - boven - 12));
        vak.scrollTo({ top: y, behavior: 'instant' });
        scrollVerwacht = y;
        stop();
        return;
      }
      if (laatsteKeer) stop();
    };
    const waarnemer = new MutationObserver((records) => {
      if (alleenHartjes(records)) return;
      check(false);
      clearTimeout(klok);
      klok = setTimeout(() => check(true), 120);
    });
    waarnemer.observe(doel, { childList: true, subtree: true });
    check(false);
    if (!klaar) {
      klok = setTimeout(() => check(true), 400);
      eind = setTimeout(stop, 1500);
    }
  };

  /* Al bij het indrukken van de muisknop in de kaart stilzetten: dat is vóór
     de focus en vóór het change-event. Komt er geen wissel, dan gaat de
     klasse na 4 s vanzelf weer weg. */
  /* De klasse staat op <html>, niet op de kaart: de morph zet het
     class-attribuut van de kaart terug naar de serverstand en dan was de
     klasse precies tussen morph en eerste tekenbeeldje weg. */
  let stilKlok = null;
  const stilzetten = (ms) => {
    document.documentElement.classList.add('erwal-stil');
    clearTimeout(stilKlok);
    stilKlok = setTimeout(() => document.documentElement.classList.remove('erwal-stil'), ms);
  };
  document.addEventListener(
    'pointerdown',
    (e) => {
      const el = e.target;
      if (!(el instanceof Element) || !el.closest('.facets--vertical')) return;
      stilzetten(4000);
    },
    true
  );

  /* De kern: om de render heen, niet aan het change-event (zie de kop van dit
     bestand). renderSection resolvet pas ná de synchrone morph, in dezelfde
     microtask-keten, dus onze correctie zit vóór het eerstvolgende tekenen. */
  let renderTeller = 0;
  const origineelRender = sectionRenderer.renderSection.bind(sectionRenderer);
  sectionRenderer.renderSection = function (sectionId, options) {
    const kaartEl = document.querySelector('.facets--vertical');
    const id = String(sectionId).replace(/^shopify-section-/, '');
    const sectie = document.getElementById('shopify-section-' + id) || document.getElementById(id);
    const hydratie = !!(options && options.mode === 'hydration');
    if (!kaartEl || hydratie || !sectie || !sectie.contains(kaartEl)) return origineelRender(sectionId, options);

    /* Eerst stilzetten, dán meten: getBoundingClientRect forceert een
       stijlberekening en die mag geen overgangen starten. */
    stilzetten(4000);
    const paneelWasOpen = paneelOpen();
    const kaartVoor = paneelWasOpen ? null : kaartPositie();
    scrollVerwacht = paneelWasOpen ? null : Math.round(scrollvak().scrollTop);
    const mijn = ++renderTeller;

    const p = origineelRender(sectionId, options);
    p.then(
      () => {
        /* Nog 600 ms stil (klikken op een kopje mag daarna weer bewegen). */
        stilzetten(600);
        /* Alleen de laatste render telt: een vorige is door Horizon afgebroken. */
        if (mijn !== renderTeller) return;
        if (paneelWasOpen) {
          zodraDicht(naarBoven);
        } else {
          kaartVasthouden(kaartVoor) || alleenAlsOnderRaster(kaartVoor);
        }
      },
      () => document.documentElement.classList.remove('erwal-stil')
    );
    return p;
  };
})();
